import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Super-admin tenant churn signals view — Feature 7.
 *
 * Reads from /v1/dashboard/churn (super_admin only). Renders a
 * severity-grouped list with per-tenant drill-down on the three
 * tracked signals (lots sold, lots created, auctions created).
 */

type Severity = 'none' | 'watch' | 'warning' | 'critical';
const SEVERITIES: readonly { key: Severity | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'warning', label: 'Warning' },
  { key: 'watch', label: 'Watch' },
  { key: 'none', label: 'None' },
];

interface SignalLine {
  signal: 'lots_sold' | 'lots_created' | 'auctions_created';
  current: number;
  prior: number;
  dropFraction: number;
}

interface ChurnSignal {
  productId: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  tenantStatus: string;
  snapshotDate: string;
  windowDays: number;
  severity: Severity;
  worstDropFraction: number;
  signals: SignalLine[];
}

interface ChurnSnapshot {
  asOfMs: number;
  windowDays: number;
  totalTenants: number;
  signals: ChurnSignal[];
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: ChurnSnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

function severityColor(s: Severity): string {
  if (s === 'critical') return 'var(--semantic-danger, #b91c1c)';
  if (s === 'warning') return 'var(--semantic-warning, #c2410c)';
  if (s === 'watch') return 'var(--semantic-info, #0369a1)';
  return 'var(--text-muted)';
}

function formatPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function SuperChurnSignals() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const params = new URLSearchParams();
    if (severityFilter !== 'all') params.set('severity', severityFilter);
    const qs = params.toString();
    const url = qs.length > 0 ? `${base}/v1/dashboard/churn?${qs}` : `${base}/v1/dashboard/churn`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as ChurnSnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [severityFilter, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Tenant churn signals
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {SEVERITIES.map((s) => (
          <button
            key={s.key}
            onClick={() => setSeverityFilter(s.key)}
            type="button"
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: severityFilter === s.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
              color: severityFilter === s.key ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {s.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading churn signals…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Churn signals unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <SignalsView data={state.data} />
      )}
    </main>
  );
}

function SignalsView({ data }: { data: ChurnSnapshot }) {
  if (data.signals.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No churn signals</CardTitle>
          <CardDescription>
            Either the daily aggregation hasn&apos;t run yet (next tick at 05:00 UTC), or no tenants
            matched the current filter.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  const date = data.signals[0]?.snapshotDate ?? '';
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Snapshot: {date} · {data.windowDays}-day window · {data.totalTenants} tenant
        {data.totalTenants === 1 ? '' : 's'}
      </div>
      {data.signals.map((s) => (
        <SignalCard key={s.tenantId} signal={s} />
      ))}
    </div>
  );
}

function SignalCard({ signal }: { signal: ChurnSignal }) {
  return (
    <Card>
      <CardHeader>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <CardTitle>
              {signal.tenantName}{' '}
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                ({signal.tenantSlug})
              </span>
            </CardTitle>
            <CardDescription>
              Tenant status: <Badge variant="outline">{signal.tenantStatus}</Badge>
            </CardDescription>
          </div>
          <div style={{ textAlign: 'right' }}>
            <SeverityBadge severity={signal.severity} />
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              worst drop: {formatPct(signal.worstDropFraction)}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <Th>Signal</Th>
                <Th align="right">Prior</Th>
                <Th align="right">Current</Th>
                <Th align="right">Drop</Th>
              </tr>
            </thead>
            <tbody>
              {signal.signals.map((line) => (
                <tr
                  key={line.signal}
                  style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}
                >
                  <Td>{labelFor(line.signal)}</Td>
                  <Td align="right">{line.prior}</Td>
                  <Td align="right">{line.current}</Td>
                  <Td align="right">
                    <span
                      style={{
                        color:
                          line.dropFraction > 0
                            ? severityColor(signal.severity)
                            : 'var(--text-muted)',
                      }}
                    >
                      {formatPct(line.dropFraction)}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '0.4rem',
        background: severityColor(severity),
        color: 'white',
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {severity}
    </span>
  );
}

function labelFor(signal: SignalLine['signal']): string {
  if (signal === 'lots_sold') return 'Lots sold';
  if (signal === 'lots_created') return 'Lots created';
  return 'Auctions created';
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '0.4rem 0.75rem',
        fontSize: '0.7rem',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ textAlign: align, padding: '0.4rem 0.75rem' }}>{children}</td>;
}
